// Address multi-fill (form-view slice, task 3.4): one Google Places pick fills
// the address field itself and its sibling fields in the SAME form, so street,
// city, state, and ZIP land as ordinary values and collapse together.
//
// The parsed bag comes from the legacy integration's `parse` (Shared/Form/
// GoogleAddressField.tsx — keys `Address:Street|City|State|PostalCode|Country|
// Full|Lat|Lng`). The label sets are ported from the legacy consumer's
// `createAddressMapping` (DynamicFormField.tsx), matched trimmed and
// case-insensitively; bag keys no sibling claims are dropped.

import { CfForm, FieldValue } from './types';
import { normalize } from './valueFormat';

export type AddressBag = Record<string, string | number>;

/** Legacy label → bag key, for the picked field's siblings. */
const SIBLING_BAG_KEYS: Record<string, string> = {
    'address 2': 'Address:Line2',
    city: 'Address:City',
    state: 'Address:State',
    country: 'Address:Country',
    postal: 'Address:PostalCode',
    zip: 'Address:PostalCode',
    'postal code': 'Address:PostalCode',
    'zip code': 'Address:PostalCode',
};

const bagText = (bag: AddressBag, key: string): string => String(bag[key] ?? '').trim();

/**
 * The fills one pick produces: the address field takes the street (falling
 * back to the formatted full address), and each sibling whose label matches a
 * legacy set takes its bag part, normalized to the field's storage format.
 * Empty bag parts produce no fill — a pick never wipes a sibling's value with
 * a blank.
 */
export const addressFillsFor = (
    form: CfForm,
    addressFieldId: string,
    bag: AddressBag
): Array<{ fieldId: string; value: FieldValue }> => {
    const fills: Array<{ fieldId: string; value: FieldValue }> = [];

    const addressField = form.fields.find((f) => f.id === addressFieldId);
    const street = bagText(bag, 'Address:Street') || bagText(bag, 'Address:Full');
    if (addressField && street) {
        fills.push({ fieldId: addressField.id, value: normalize(addressField, street) });
    }

    form.fields.forEach((field) => {
        if (field.id === addressFieldId) return;
        const key = SIBLING_BAG_KEYS[field.label.trim().toLowerCase()];
        if (!key) return;
        const part = bagText(bag, key);
        if (!part) return;
        fills.push({ fieldId: field.id, value: normalize(field, part) });
    });

    return fills;
};
